/**
 * HUDConfig — Toggle visibility of HUD elements.
 *
 * Saves preferences to localStorage. Shows panel with checkboxes
 * for each HUD component: minimap, compass, mana bar, quest tracker,
 * performance stats, crosshair, hotbar, healthbar.
 */

const STORAGE_KEY = 'fedeworld_hud_config';

const HUD_ELEMENTS = [
  { key: 'crosshair', label: 'Mira (crosshair)', defaultOn: true },
  { key: 'hotbar', label: 'Hotbar', defaultOn: true },
  { key: 'healthbar', label: 'Barra de vida/hambre', defaultOn: true },
  { key: 'minimap', label: 'Minimapa', defaultOn: true },
  { key: 'manabar', label: 'Barra de mana', defaultOn: true },
  { key: 'spellbar', label: 'Barra de hechizos', defaultOn: true },
  { key: 'questTracker', label: 'Tracker de misiones', defaultOn: true },
  { key: 'compass', label: 'Brújula', defaultOn: true },
  { key: 'fps', label: 'Contador de FPS', defaultOn: true },
  { key: 'coords', label: 'Coordenadas XYZ', defaultOn: true },
];

export class HUDConfig {
  constructor() {
    this.settings = this._load();
    this._buildUI();
    this._apply();
  }

  _load() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const defaults = {};
      for (const el of HUD_ELEMENTS) defaults[el.key] = saved[el.key] ?? el.defaultOn;
      return defaults;
    } catch {
      const defaults = {};
      for (const el of HUD_ELEMENTS) defaults[el.key] = el.defaultOn;
      return defaults;
    }
  }

  _save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
  }

  _apply() {
    // Toggle visibility via body classes (other components check these)
    const body = document.body;
    for (const el of HUD_ELEMENTS) {
      const className = 'fw-hide-' + el.key;
      if (this.settings[el.key]) {
        body.classList.remove(className);
      } else {
        body.classList.add(className);
      }
    }
    this._injectHideCSS();
  }

  _injectHideCSS() {
    if (document.getElementById('fw-hud-hide-style')) return;
    const style = document.createElement('style');
    style.id = 'fw-hud-hide-style';
    // Selectors for elements to hide (by ID or class). These IDs must match game code.
    style.textContent = `
      .fw-hide-crosshair .hud-crosshair,
      .fw-hide-crosshair #hud-crosshair { display: none !important; }
      .fw-hide-hotbar #hotbar { display: none !important; }
      .fw-hide-healthbar #healthbar-container { display: none !important; }
      .fw-hide-minimap #minimap-container { display: none !important; }
    `;
    document.head.appendChild(style);
  }

  isVisible(key) {
    return this.settings[key] !== false;
  }

  _buildUI() {
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      background:rgba(0,0,0,0.78); z-index:917; display:none;
      justify-content:center; align-items:center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#2A2A2A; border:4px solid #4A4A4A;
      padding:24px; min-width:380px;
      font-family:'Courier New',monospace; color:#DDD;
    `;

    const title = document.createElement('div');
    title.textContent = '🎛 Configuración de HUD';
    title.style.cssText = 'font-size:20px;font-weight:bold;color:#FFD700;margin-bottom:16px;text-align:center;';
    panel.appendChild(title);

    const desc = document.createElement('div');
    desc.textContent = 'Activa/desactiva elementos de la pantalla';
    desc.style.cssText = 'font-size:11px;color:#999;margin-bottom:14px;text-align:center;';
    panel.appendChild(desc);

    for (const el of HUD_ELEMENTS) {
      const row = document.createElement('label');
      row.style.cssText = `
        display:flex;align-items:center;padding:8px 10px;margin-bottom:4px;
        background:#3A3A3A;cursor:pointer;border-left:3px solid transparent;
        transition:background 0.15s, border-color 0.15s;
      `;
      row.addEventListener('mouseenter', () => {
        row.style.background = '#4A4A4A';
        row.style.borderLeftColor = '#FFD700';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = '#3A3A3A';
        row.style.borderLeftColor = 'transparent';
      });
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = this.settings[el.key];
      cb.style.marginRight = '10px';
      cb.addEventListener('change', () => {
        this.settings[el.key] = cb.checked;
        this._save();
        this._apply();
      });
      row.appendChild(cb);
      const label = document.createElement('span');
      label.textContent = el.label;
      label.style.fontSize = '13px';
      row.appendChild(label);
      panel.appendChild(row);
    }

    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.cssText = `
      margin-top:16px; padding:10px 0; text-align:center; cursor:pointer;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      font-size:14px; font-weight:bold;
    `;
    closeBtn.addEventListener('click', () => this.hide());
    panel.appendChild(closeBtn);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  show() { this._overlay.style.display = 'flex'; this.isOpen = true; }
  hide() { this._overlay.style.display = 'none'; this.isOpen = false; }
}
